import React, { useCallback, useMemo, useState } from 'react';
import { BarChart3, ChevronDown, ChevronUp, RotateCcw } from 'lucide-react';
import toast from 'react-hot-toast';
import DashboardBlockCard from './DashboardBlockCard';
import InspectBlockDrawer from './InspectBlockDrawer';
import ControlBar from './controls/ControlBar';
import { useDashboardBuild } from '@/contexts/DashboardBuildContext';
import type { RenderHandle, DashboardBlock, Position } from '@/types/dashboard';

const BLOCK_TYPE_LABELS: Record<string, string> = {
  'clustered-column': 'Column Chart',
  'stacked-column': 'Stacked Column',
  'horizontal-bar': 'Bar Chart',
  'line': 'Line Chart',
  'area': 'Area Chart',
  'pie': 'Pie Chart',
  'donut': 'Donut Chart',
  'data-table': 'Data Table',
  'kpi-card': 'KPI Card',
};

const EmptyCanvas: React.FC = () => (
  <div className="flex-1 flex items-center justify-center">
    <div className="text-center max-w-sm px-4">
      <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
        <BarChart3 className="w-8 h-8 text-muted-foreground" />
      </div>
      <h3 className="text-lg font-medium text-foreground mb-2">
        Your canvas is empty
      </h3>
      <p className="text-sm text-muted-foreground">
        Charts will appear here as the AI builds them. Start by describing
        what you want in the chat panel.
      </p>
    </div>
  </div>
);

const BuildCanvasPanel: React.FC = () => {
  const { state, renderHandles, updateBlockStatus, sendFeedback, updateBlockPositions, changeControlValue } = useDashboardBuild();
  const { blocks, blockData, dashboard, controls, activeControlValues, isPreviewMode, isRefreshing } = state;

  const [rejectedTrayOpen, setRejectedTrayOpen] = useState(false);
  const [inspectBlockId, setInspectBlockId] = useState<string | null>(null);

  const layoutColumns = dashboard?.layout_columns ?? 2;

  const handleRenderHandle = useCallback(
    (handle: RenderHandle) => {
      renderHandles.current.set(handle.blockId, handle);
    },
    [renderHandles]
  );

  const handleDisposeHandle = useCallback(
    (blockId: string) => {
      renderHandles.current.delete(blockId);
    },
    [renderHandles]
  );

  const handleKeep = useCallback(
    (blockId: string) => {
      updateBlockStatus(blockId, 'accepted');
    },
    [updateBlockStatus]
  );

  const handleDiscard = useCallback(
    (blockId: string) => {
      const block = blocks.get(blockId);
      const title = block?.title ?? 'Block';

      updateBlockStatus(blockId, 'rejected');

      toast(
        (t) => (
          <div className="flex items-center gap-3">
            <span className="text-sm">
              Removed <strong>{title}</strong>
            </span>
            <button
              onClick={() => {
                updateBlockStatus(blockId, 'draft');
                toast.dismiss(t.id);
              }}
              className="text-sm font-medium text-primary hover:text-primary"
            >
              Undo
            </button>
          </div>
        ),
        { duration: 10000 }
      );
    },
    [blocks, updateBlockStatus]
  );

  const handleUndoKeep = useCallback(
    (blockId: string) => {
      updateBlockStatus(blockId, 'draft');
    },
    [updateBlockStatus]
  );

  const handleFeedback = useCallback(
    (blockId: string, blockTitle: string, feedback: string) => {
      sendFeedback(blockId, blockTitle, feedback);
    },
    [sendFeedback]
  );

  const handleRestore = useCallback(
    (blockId: string) => {
      updateBlockStatus(blockId, 'draft');
    },
    [updateBlockStatus]
  );

  const handleReorder = useCallback(
    (blockId: string, direction: 'up' | 'down') => {
      const sorted = Array.from(blocks.values())
        .filter((b) => b.status === 'accepted')
        .sort((a, b) => {
          if (a.position.y !== b.position.y) return a.position.y - b.position.y;
          return a.position.x - b.position.x;
        });

      const idx = sorted.findIndex((b) => b.id === blockId);
      if (idx < 0) return;

      const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (swapIdx < 0 || swapIdx >= sorted.length) return;

      const current = sorted[idx];
      const other = sorted[swapIdx];
      updateBlockPositions([
        { blockId: current.id, position: { ...current.position, y: other.position.y, x: other.position.x } },
        { blockId: other.id, position: { ...other.position, y: current.position.y, x: current.position.x } },
      ]);
    },
    [blocks, updateBlockPositions]
  );

  const handleResize = useCallback(
    (blockId: string, position: Position) => {
      updateBlockPositions([{ blockId, position }]);
    },
    [updateBlockPositions]
  );

  const handleInspect = useCallback((blockId: string) => {
    setInspectBlockId(blockId);
  }, []);

  const handleCloseInspect = useCallback(() => {
    setInspectBlockId(null);
  }, []);

  const inspectBlock = inspectBlockId ? blocks.get(inspectBlockId) ?? null : null;
  const inspectData = inspectBlockId ? blockData.get(inspectBlockId) ?? [] : null;

  const sortedBlocks: DashboardBlock[] = useMemo(() => {
    return Array.from(blocks.values())
      .filter((b) => isPreviewMode ? b.status === 'accepted' : b.status !== 'rejected')
      .sort((a, b) => {
        if (a.position.y !== b.position.y) return a.position.y - b.position.y;
        return a.position.x - b.position.x;
      });
  }, [blocks, isPreviewMode]);

  const rejectedBlocks: DashboardBlock[] = useMemo(() => {
    return Array.from(blocks.values()).filter((b) => b.status === 'rejected');
  }, [blocks]);

  const hasActiveBlocks = sortedBlocks.length > 0;
  const hasRejectedBlocks = rejectedBlocks.length > 0;

  if (!hasActiveBlocks && !hasRejectedBlocks) {
    return (
      <div className="flex flex-col h-full bg-background/50">
        <ControlBar
          controls={Array.from(controls.values())}
          activeControlValues={activeControlValues}
          isRefreshing={isRefreshing}
          onChangeControlValue={changeControlValue}
        />
        <EmptyCanvas />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-background/50 overflow-y-auto">
      {/* Control bar */}
      <ControlBar
        controls={Array.from(controls.values())}
        activeControlValues={activeControlValues}
        isRefreshing={isRefreshing}
        onChangeControlValue={changeControlValue}
      />

      {/* Canvas header */}
      <div className="flex-shrink-0 px-4 py-3 border-b border-border bg-background">
        <h2 className="text-sm font-semibold text-foreground">
          {isPreviewMode ? 'Preview' : 'Canvas'}
        </h2>
        <p className="text-xs text-muted-foreground">
          {sortedBlocks.length} block{sortedBlocks.length !== 1 ? 's' : ''}
          {!isPreviewMode && hasRejectedBlocks && ` · ${rejectedBlocks.length} discarded`}
        </p>
      </div>

      {/* Block grid */}
      {hasActiveBlocks && (
        <div
          className="flex-1 p-4 gap-4"
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${layoutColumns}, 1fr)`,
            alignContent: 'start',
          }}
        >
          {sortedBlocks.map((block) => {
            const data = blockData.get(block.id) ?? [];
            const spanW = Math.min(block.position.w, layoutColumns);

            return (
              <div key={block.id} style={{ gridColumn: `span ${spanW}` }}>
                <DashboardBlockCard
                  block={block}
                  data={data}
                  previewMode={isPreviewMode}
                  onRenderHandle={handleRenderHandle}
                  onDisposeHandle={handleDisposeHandle}
                  onKeep={handleKeep}
                  onDiscard={handleDiscard}
                  onFeedback={handleFeedback}
                  onUndoKeep={handleUndoKeep}
                  onReorder={handleReorder}
                  onResize={handleResize}
                  onInspect={handleInspect}
                />
              </div>
            );
          })}
        </div>
      )}

      {!hasActiveBlocks && hasRejectedBlocks && <EmptyCanvas />}

      {/* Rejected tray — hidden in preview mode */}
      {!isPreviewMode && hasRejectedBlocks && (
        <div className="flex-shrink-0 border-t border-border">
          <button
            onClick={() => setRejectedTrayOpen((v) => !v)}
            className="w-full flex items-center justify-between px-4 py-2.5 bg-background hover:bg-muted transition-colors"
          >
            <span className="text-xs font-medium text-muted-foreground">
              Discarded ({rejectedBlocks.length})
            </span>
            {rejectedTrayOpen ? (
              <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
            ) : (
              <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" />
            )}
          </button>

          {rejectedTrayOpen && (
            <div className="px-4 pb-3 bg-background space-y-1.5">
              {rejectedBlocks.map((block) => (
                <div
                  key={block.id}
                  className="flex items-center justify-between px-3 py-2 rounded-lg bg-muted border border-border"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm text-muted-foreground truncate">
                      {block.title}
                    </span>
                    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-muted text-muted-foreground flex-shrink-0">
                      {BLOCK_TYPE_LABELS[block.block_type] ?? block.block_type}
                    </span>
                  </div>
                  <button
                    onClick={() => handleRestore(block.id)}
                    className="flex items-center gap-1 text-xs font-medium text-primary hover:text-primary flex-shrink-0 ml-3 transition-colors"
                  >
                    <RotateCcw className="w-3 h-3" />
                    Restore
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Inspect drawer */}
      {inspectBlock && inspectData && (
        <InspectBlockDrawer
          isOpen={!!inspectBlock}
          onClose={handleCloseInspect}
          block={inspectBlock}
          data={inspectData}
        />
      )}
    </div>
  );
};

export default BuildCanvasPanel;
